import { AlertMessageService } from 'src/app/modules/shared/services/alertmessage.service';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { LanguageService } from '../../shared/services/language.service';
import { ProfileService } from '../../shared/services/profile.service';
import { InfoBarWithStatusService } from '../../shared/services/info-bar-with-status.service';
import { Profile } from 'src/app/models/Profile';
import { FormControl } from '@angular/forms';
import { PerfilesDialogComponent } from '../perfiles-dialog/perfiles-dialog.component';
import { RemoveDialogComponent } from '../remove-dialog/remove-dialog.component';
import { forkJoin } from 'rxjs';
import { AuthService } from '../../shared/services/auth.service';
import { PermissionService } from '../../shared/services/permission.service';
import { Permission } from '../../shared/security/permission';

interface CenterKPI {
    center: string;
    count: number;
    averageCost: number;
}

@Component({
    standalone: false,
    selector: 'app-perfiles',
    templateUrl: './perfiles.component.html',
    styleUrls: ['./perfiles.component.css']
})
export class PerfilesComponent implements OnInit, AfterViewInit {
    private readonly noCenterFilterToken = '__no_center__';

    Permission = Permission;
    variables: any;
    profiles = new MatTableDataSource<Profile>([]);
    usageCounts: { [profileId: string]: number } = {};
    displayedColumns: string[] = ['name', 'center', 'description', 'costPerHour', 'lastModifiedDate', 'usage', 'actions'];
    isLoading = true;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) set sort(value: MatSort) {
        if (value) {
            this.profiles.sort = value;
        }
    }
    isRestrictedRole = false;

    showKPIs = false;
    showCostCenterFilter = true;
    selectedCostCenter = '';
    costCenterSearchCtrl = new FormControl<string>('', { nonNullable: true });
    availableCostCenters: string[] = [];
    filteredAvailableCostCenters: string[] = [];

    // KPIs
    totalProfiles = 0;
    averageCost = 0;
    centerKPIs: CenterKPI[] = [];

    constructor(
        private profileService: ProfileService,
        private languageService: LanguageService,
        private infoBar: InfoBarWithStatusService,
        public authService: AuthService,
        private permissionService: PermissionService,
        public dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.profiles.sortData = (data: Profile[]) => {
            return [...data].sort((a, b) => {
                const aName = (a?.name || '').toLocaleLowerCase();
                const bName = (b?.name || '').toLocaleLowerCase();
                return aName.localeCompare(bName);
            });
        };

        this.profiles.sortingDataAccessor = (item: Profile, property: string): string | number => {
            const value = (item as any)[property];

            if (typeof value === 'string') {
                return value.toLocaleLowerCase();
            }

            return value ?? '';
        };

        this.initializeCostCenterFilter();
        this.costCenterSearchCtrl.valueChanges.subscribe((searchTerm) => {
            this.filterAvailableCostCenters(searchTerm || '');
        });
        this.languageService.translations$.subscribe((vars: any) => {
            this.variables = vars;
            this.filterAvailableCostCenters(this.costCenterSearchCtrl.value || '');
        });
        this.authService.getUserData().subscribe((user) => {
            this.isRestrictedRole = !this.permissionService.hasPermission(Permission.PROFILE_MANAGE);
        });
        this.loadProfiles();
    }


    ngAfterViewInit(): void {
        this.profiles.paginator = this.paginator;
    }

    loadProfiles(): void {
        this.isLoading = true;
        forkJoin({
            profiles: this.profileService.getAllProfiles(),
            usageCounts: this.profileService.getProfileUsageCounts()
        }).subscribe({
            next: (result) => {
                this.profiles.data = result.profiles;
                this.usageCounts = result.usageCounts;
                this.buildAvailableCostCenters();
                this.computeKPIs();
                this.isLoading = false;
            },
            error: (err) => {
                this.isLoading = false;
                this.infoBar.showSnackbar(this.variables?.errorLoadingProfiles || 'Error loading profiles', ['error-snackbar'], 3000);
            }
        });
    }

    computeKPIs(): void {
        this.totalProfiles = this.profiles.data.length;
        if (this.profiles.data.length > 0) {
            const totalCost = this.profiles.data.reduce((sum, p) => sum + (p.costPerHour || 0), 0);
            this.averageCost = totalCost / this.profiles.data.length;
        } else {
            this.averageCost = 0;
        }

        // KPIs by center
        const centerMap = new Map<string, { total: number; count: number }>();
        for (const p of this.profiles.data) {
            const center = p.center || (this.variables?.noCenter || 'Sin centro');
            const entry = centerMap.get(center) || { total: 0, count: 0 };
            entry.total += (p.costPerHour || 0);
            entry.count += 1;
            centerMap.set(center, entry);
        }
        this.centerKPIs = Array.from(centerMap.entries())
            .map(([center, data]) => ({
                center,
                count: data.count,
                averageCost: data.total / data.count
            }))
            .sort((a, b) => a.center.localeCompare(b.center));
    }

    private initializeCostCenterFilter(): void {
        this.profiles.filterPredicate = (data: Profile, filter: string) => {
            if (!filter) {
                return true;
            }

            const center = (data.center || '').trim().toLowerCase();
            if (filter === this.noCenterFilterToken) {
                return center.length === 0;
            }

            return center === filter;
        };
    }

    private buildAvailableCostCenters(): void {
        const centers = new Set<string>();

        for (const profile of this.profiles.data) {
            const center = (profile.center || '').trim().toLowerCase();
            centers.add(center || this.noCenterFilterToken);
        }

        this.availableCostCenters = Array.from(centers).sort((a, b) => {
            if (a === this.noCenterFilterToken) return 1;
            if (b === this.noCenterFilterToken) return -1;
            return a.localeCompare(b);
        });

        this.filterAvailableCostCenters(this.costCenterSearchCtrl.value || '');

        if (this.selectedCostCenter && !this.availableCostCenters.includes(this.selectedCostCenter)) {
            this.selectedCostCenter = '';
            this.profiles.filter = '';
        }
    }

    private filterAvailableCostCenters(searchTerm: string): void {
        const normalized = (searchTerm || '').trim().toLocaleLowerCase();

        if (!normalized) {
            this.filteredAvailableCostCenters = [...this.availableCostCenters];
            return;
        }

        this.filteredAvailableCostCenters = this.availableCostCenters.filter((center) =>
            this.getCostCenterLabel(center).toLocaleLowerCase().includes(normalized)
        );
    }

    applyCostCenterFilter(value: string): void {
        this.selectedCostCenter = value || '';
        this.profiles.filter = this.selectedCostCenter;
        if (this.paginator) {
            this.paginator.firstPage();
        }
    }

    getCostCenterLabel(center: string): string {
        return center === this.noCenterFilterToken ? (this.variables?.noCenter || 'Sin centro') : center;
    }
    private getErrorMessage(err: any, fallbackKey: string): string {
        const serverMessage = err?.error?.message || err?.error;

        // Map server error messages to translation keys
        if (typeof serverMessage === 'string') {
            if (serverMessage.toLowerCase().includes('profile with this name already exists')) {
                return this.variables?.profileNameAlreadyExists || serverMessage;
            }
            else if (serverMessage.toLowerCase().includes('it is referenced in at least one catalog')) {
                return this.variables?.cannotDeleteProfileReferenced || serverMessage;

            }
        }

        // Fallback to generic error message
        return this.variables?.[fallbackKey] || serverMessage || 'An error occurred';
    }
    getUsageCount(profile: Profile): number {
        return profile.id ? (this.usageCounts[profile.id] || 0) : 0;
    }

    openAddDialog(): void {
        const dialogRef = this.dialog.open(PerfilesDialogComponent, {
            width: '500px',
            data: {
                title: this.variables?.addProfile || 'Agregar Perfil',
                isEdit: false,
                profile: { name: '', description: '', costPerHour: 0, center: '' }
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.data?.success) {
                this.loadProfiles();
            }
        });
    }

    openEditDialog(profile: Profile): void {
        const dialogRef = this.dialog.open(PerfilesDialogComponent, {
            width: '500px',
            data: {
                title: this.variables?.editProfile || 'Editar Perfil',
                isEdit: true,
                profile: { ...profile }
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.data?.success) {
                this.loadProfiles();
            }
        });
    }

    deleteProfile(profile: Profile): void {
        const dialogRef = this.dialog.open(RemoveDialogComponent);

        dialogRef.afterClosed().subscribe(result => {
            if (result === 'true') {
                this.profileService.deleteProfile(profile.id!).subscribe({
                    next: () => {
                        this.infoBar.showSnackbar(this.variables?.profileRemovedSuccessfully || 'Profile deleted', ['successful-snackbar'], 2000);
                        this.loadProfiles();
                    },
                    error: (err) => {
                        console.log(err);

                        const msg = this.getErrorMessage(err, 'errorDeletingProfile');
                        this.infoBar.showSnackbar(msg, ['error-snackbar'], 3000);
                    }
                });
            }
        });
    }
}
